import React, { useState, useEffect } from 'react';
import {
  CloudServerOutlined,
  TeamOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';
import api from '../../services/api';

const PlatformMetrics = () => {
  const [loading, setLoading] = useState(true);
  const [platformData, setPlatformData] = useState(null);
  const [timeRange, setTimeRange] = useState(7); // days
  const [refreshInterval, setRefreshInterval] = useState(null);

  useEffect(() => {
    fetchPlatformMetrics();

    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchPlatformMetrics();
    }, 30000);

    setRefreshInterval(interval);

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [timeRange]);

  const fetchPlatformMetrics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/analytics/platform-usage?days=${timeRange}`);
      setPlatformData(response.data);
    } catch (error) {
      console.error('Error fetching platform metrics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading && !platformData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading platform metrics...</div>
      </div>
    );
  }

  if (!platformData) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">No platform data available</div>
      </div>
    );
  }

  // Chart colors
  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444'];

  // System health status
  const getHealthStatus = () => {
    const status = platformData.system_health?.status || 'unknown';
    if (status === 'operational') {
      return {
        color: 'green',
        icon: CheckCircleOutlined,
        text: 'Operational',
      };
    }
    return {
      color: 'yellow',
      icon: WarningOutlined,
      text: 'Warning',
    };
  };

  const healthStatus = getHealthStatus();

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="bg-white shadow rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Platform Metrics & System Health
            </h1>
            <p className="text-gray-600">
              Real-time monitoring and analytics (Last {timeRange} days)
            </p>
          </div>
          <div className="flex items-center space-x-2">
            <select
              value={timeRange}
              onChange={(e) => setTimeRange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm"
            >
              <option value={7}>Last 7 days</option>
              <option value={14}>Last 14 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
            <button
              onClick={fetchPlatformMetrics}
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* System Health Status */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          System Health Status
        </h2>
        <div className="flex items-center space-x-4">
          <div className={`p-3 rounded-lg bg-${healthStatus.color}-50`}>
            <healthStatus.icon className={`h-8 w-8 text-${healthStatus.color}-600`} />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{healthStatus.text}</p>
            <p className="text-sm text-gray-500">All systems operational</p>
          </div>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <MetricCard
          title="Active Sessions Now"
          value={platformData.active_sessions_now}
          icon={CloudServerOutlined}
          color="blue"
          trend="real-time"
        />
        <MetricCard
          title="Total Users"
          value={platformData.total_registered_users}
          icon={TeamOutlined}
          color="green"
        />
        <MetricCard
          title="Active Users (Period)"
          value={platformData.active_users_period}
          icon={TeamOutlined}
          color="purple"
        />
        <MetricCard
          title="Completion Rate"
          value={`${platformData.completion_rate}%`}
          icon={CheckCircleOutlined}
          color="orange"
        />
      </div>

      {/* Session Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Total Sessions
          </h3>
          <p className="text-3xl font-bold text-gray-900">
            {platformData.total_sessions_period}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            Last {platformData.period_days} days
          </p>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Completed Sessions
          </h3>
          <p className="text-3xl font-bold text-green-600">
            {platformData.completed_sessions_period}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            {platformData.completion_rate}% completion rate
          </p>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 mb-2">
            Avg Session Duration
          </h3>
          <p className="text-3xl font-bold text-purple-600">
            {platformData.average_session_duration_minutes.toFixed(0)}
          </p>
          <p className="text-sm text-gray-500 mt-1">minutes</p>
        </div>
      </div>

      {/* Daily Trends */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Session Activity Trends
        </h2>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart data={platformData.daily_trends}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="date" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area
              type="monotone"
              dataKey="total_sessions"
              stroke="#3B82F6"
              fill="#3B82F6"
              fillOpacity={0.6}
              name="Total Sessions"
            />
            <Area
              type="monotone"
              dataKey="completed_sessions"
              stroke="#10B981"
              fill="#10B981"
              fillOpacity={0.6}
              name="Completed Sessions"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* User Distribution & System Resources */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* User Role Distribution */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            User Role Distribution
          </h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={platformData.user_role_distribution}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ role, count }) => `${role}: ${count}`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="count"
              >
                {platformData.user_role_distribution?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 space-y-2">
            {platformData.user_role_distribution?.map((role, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <div className="flex items-center">
                  <div
                    className="w-3 h-3 rounded-full mr-2"
                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                  />
                  <span className="text-gray-700 capitalize">{role.role}</span>
                </div>
                <span className="font-semibold text-gray-900">{role.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* System Resources */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            System Resources
          </h2>
          <div className="space-y-4">
            <ResourceIndicator
              label="AI Token Consumption"
              value={platformData.system_health?.ai_tokens_consumed_period || 0}
              unit="tokens"
              max={1000000}
              color="blue"
            />
            <ResourceIndicator
              label="Database Connections"
              value={platformData.system_health?.database_connections || 0}
              unit="connections"
              max={100}
              color="green"
            />
            <ResourceIndicator
              label="Avg Response Time"
              value={platformData.system_health?.avg_response_time_ms || 0}
              unit="ms"
              max={1000}
              color="purple"
            />
          </div>

          {/* Quick Stats */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Quick Statistics
            </h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Active Sessions Now</span>
                <span className="font-semibold text-gray-900">
                  {platformData.active_sessions_now}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Peak Sessions Today</span>
                <span className="font-semibold text-gray-900">
                  {Math.max(...(platformData.daily_trends?.map(d => d.total_sessions) || [0]))}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">System Uptime</span>
                <span className="font-semibold text-green-600">99.9%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ title, value, icon: Icon, color, trend }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    purple: 'bg-purple-50 text-purple-600',
    orange: 'bg-orange-50 text-orange-600',
  };

  return (
    <div className="bg-white shadow rounded-lg p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500 mb-1">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          {trend && (
            <p className="text-xs text-blue-600 mt-1 flex items-center">
              <span className="inline-block w-2 h-2 bg-blue-600 rounded-full mr-1 animate-pulse" />
              {trend}
            </p>
          )}
        </div>
        <div className={`p-3 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
};

// Resource Indicator Component
const ResourceIndicator = ({ label, value, unit, max, color }) => {
  const percentage = Math.min((value / max) * 100, 100);

  const colorClasses = {
    blue: 'bg-blue-500',
    green: 'bg-green-500',
    purple: 'bg-purple-500',
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-gray-700">{label}</span>
        <span className="text-sm font-semibold text-gray-900">
          {value.toLocaleString()} {unit}
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${colorClasses[color]}`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default PlatformMetrics;
